import type { SurveyAnswer } from '@/state/surveys/useSurveyResults';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from 'helpers/cn';
import { formatDateTime } from 'helpers/formatters';
import { Button } from '@/components/ui/button';
import { useSurveyFeed } from '@/state/surveys/useSurveyFeed';
import {
  CHIP,
  FILTER_PILL,
  FILTER_PILL_OFF,
  FILTER_PILL_ON,
  PANEL,
  PANEL_HEAD,
  PANEL_TITLE,
  SENTIMENT_STYLES,
} from './result-styles';

type ResponsesFeedProps = {
  surveyId: number;
  /** A question's label by id; answers to a question since removed still need one. */
  labelOf: (questionId: number) => string;
};

/** What one answer said, as text: its score, its choice or choices, or nothing for a comment. */
const answerValue = (answer: SurveyAnswer): string | null => {
  if (answer.score !== null) return String(answer.score);
  if (answer.choices && answer.choices.length > 0) return answer.choices.join(', ');
  return answer.choice;
};

const FILTERS = ['all', 'positive', 'neutral', 'negative'];

/**
 * The responses feed, one entry per submission, with its own page and filters.
 * Its own component so a page or filter click re-renders this list alone, not the
 * charts above it.
 */
export const ResponsesFeed = ({ surveyId, labelOf }: ResponsesFeedProps) => {
  const { feed, isFetching, page, setPage, sentiment, setFilter, withComment, toggleComments } =
    useSurveyFeed(surveyId);

  return (
    <section className={PANEL}>
      <div className={PANEL_HEAD}>
        <h2 className={PANEL_TITLE}>Responses</h2>
        {/* One swipeable line below `md` rather than a wrapping block: five pills
            wrapping to three rows pushes the responses they filter off screen. */}
        <div
          className="-mx-1 flex w-full items-center gap-1.5 overflow-x-auto px-1 md:w-auto md:flex-wrap md:overflow-visible"
          data-tour="survey-results-filters"
          role="group"
          aria-label="Filter responses"
        >
          {FILTERS.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setFilter(value)}
              aria-pressed={sentiment === value}
              className={cn(FILTER_PILL, sentiment === value ? FILTER_PILL_ON : FILTER_PILL_OFF)}
            >
              {value}
            </button>
          ))}
          <button
            type="button"
            onClick={toggleComments}
            aria-pressed={withComment}
            className={cn(FILTER_PILL, withComment ? FILTER_PILL_ON : FILTER_PILL_OFF)}
          >
            With comment
          </button>
        </div>
      </div>

      <div
        className={cn('flex flex-col gap-2 p-3 transition-opacity', isFetching && 'opacity-60')}
        aria-busy={isFetching}
      >
        {feed?.submissions.length === 0 && (
          <p className="supporting px-1 py-6 text-center">No responses match these filters.</p>
        )}
        {feed?.submissions.map((submission, idx) => (
          <div
            key={submission.key}
            data-tour={idx === 0 ? 'survey-results-response' : undefined}
            className="bg-panel2 border-line rounded-card hover:bg-hover border p-3 transition-colors duration-150"
          >
            {/* One person's submit: every answer it carried, in question order. */}
            <dl className="m-0 flex flex-col gap-2">
              {submission.answers.map((answer) => {
                const value = answerValue(answer);
                return (
                  <div key={answer.id} className="min-w-0">
                    <dt className="micro-label">{labelOf(answer.questionId)}</dt>
                    <dd className="m-0 mt-0.5 flex flex-wrap items-baseline gap-2">
                      {value !== null && <span className="text-foreground num text-meta break-words">{value}</span>}
                      {answer.comment && (
                        <span className="text-foreground text-meta break-words">{answer.comment}</span>
                      )}
                      {answer.sentiment && (
                        <span className={cn(CHIP, SENTIMENT_STYLES[answer.sentiment])}>{answer.sentiment}</span>
                      )}
                    </dd>
                  </div>
                );
              })}
            </dl>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="micro-label">{formatDateTime(submission.timestamp)}</span>
              {submission.device && <span className="micro-label">· {submission.device}</span>}
            </div>
          </div>
        ))}
      </div>

      {feed && feed.pageCount > 1 && (
        /*
          A local pager rather than the shared PaginationBar, which takes a whole
          useDataControl controller and is therefore tied to client-side paging.
          This feed is server-paginated because the response set is unbounded.
        */
        <div className="border-line bg-panel2 rounded-b-panel flex items-center justify-between border-t px-4 py-2.5">
          <p className="micro-label m-0">
            Page {feed.page} of {feed.pageCount} · {feed.total} responses
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              className="border-input text-dim hover:text-foreground size-9 rounded-lg bg-transparent p-0 md:size-7"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="sr-only">Previous page</span>
            </Button>
            <Button
              variant="outline"
              className="border-input text-dim hover:text-foreground size-9 rounded-lg bg-transparent p-0 md:size-7"
              disabled={page >= feed.pageCount}
              onClick={() => setPage(page + 1)}
            >
              <ChevronRight className="h-4 w-4" />
              <span className="sr-only">Next page</span>
            </Button>
          </div>
        </div>
      )}
    </section>
  );
};
